import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { HardHat, FileSpreadsheet, Search } from "lucide-react";
import { api, BACKEND, getToken } from "@/lib/api";
import { ORDER_STEPS, ORDER_TONE, fmtDay, fmtEuro } from "@/lib/format";
import { Empty, PageHeader, Pill, Spinner } from "@/components/rp";
import { ORDER_APPT_KINDS } from "@/lib/format";
import { CantiereCard } from "@/components/CantiereCard";
import { AdminOverview } from "@/pages/admin/AdminCantieri";

const Bar = ({ label, value, tone, testid }) => (
  <div className="w-full sm:w-44" data-testid={testid}>
    <div className="flex justify-between text-xs mb-1"><span className="text-[#757575]">{label}</span><span className="font-semibold">{value}%</span></div>
    <div className="h-2 rounded-full bg-[#EBEBEB] overflow-hidden"><div className={`h-full rounded-full ${tone}`} style={{ width: `${Math.min(value, 100)}%` }} /></div>
  </div>
);

const OrderRow = ({ o }) => (
  <Link to={`/admin/commesse/${o.id}`} className="rp-card p-4 sm:px-6 flex flex-wrap items-center gap-4 hover:border-[#C8BDAB] transition-colors" data-testid={`order-row-${o.id}`}>
    <div className="flex-1 min-w-[220px]">
      <p className="text-xs font-mono text-[#757575]">{o.code}</p>
      <p className="font-medium">{o.title}</p>
      <p className="text-sm text-[#757575]">{o.client_name}{o.next_appointment && ` · ${ORDER_APPT_KINDS[o.next_appointment.kind]} ${fmtDay(o.next_appointment.date)}`}</p>
    </div>
    <Bar label="Avanzamento lavori" value={o.work_percent ?? 0} tone="bg-[#070707]" testid={`order-work-${o.id}`} />
    <div className="w-full sm:w-52 space-y-1" data-testid={`order-pay-${o.id}`}>
      <Bar label="Pagato sul totale" value={o.paid_percent ?? 0} tone="bg-[#198754]" />
      <p className="text-xs text-[#757575]"><b className="text-black">{fmtEuro(o.paid_amount)}</b> di <b className="text-black" data-testid={`order-total-${o.id}`}>{fmtEuro(o.contract_amount)}</b> · da incassare {fmtEuro(o.remaining_amount)}</p>
    </div>
    <div className="flex flex-col items-end gap-1">
      <Pill tone={ORDER_TONE(o.status)}>{o.status_label}</Pill>
      <span className="text-xs text-[#757575]">{o.steps_done} di {o.steps_total} fasi</span>
    </div>
  </Link>
);

const Totals = ({ list }) => {
  const tot = list.reduce((s, o) => s + (o.contract_amount || 0), 0);
  const paid = list.reduce((s, o) => s + (o.paid_amount || 0), 0);
  const work = list.length ? Math.round(list.reduce((s, o) => s + (o.work_percent || 0), 0) / list.length) : 0;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4" data-testid="orders-totals">
      {[["Cantieri", list.length], ["Totale contratti", fmtEuro(tot)], ["Incassato", `${fmtEuro(paid)} (${tot ? Math.round((paid / tot) * 100) : 0}%)`], ["Avanzamento medio lavori", `${work}%`]].map(([l, v]) => (
        <div key={l} className="rp-card p-4"><p className="rp-label">{l}</p><p className="text-xl font-semibold mt-1" data-testid={`orders-total-${l.split(" ")[0].toLowerCase()}`}>{v}</p></div>
      ))}
    </div>
  );
};

const Unlinked = () => {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/cantieri-senza-contratto").then((r) => setList(r.data)); }, []);
  if (!list.length) return null;
  return (
    <div className="mt-10" data-testid="unlinked-cantieri">
      <p className="rp-label mb-1">Cantieri senza contratto</p>
      <p className="text-sm text-[#757575] mb-4">Cantieri creati senza preventivo: hanno diario lavori e foto, ma non fasi contrattuali e pagamenti.</p>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">{list.map((c) => <CantiereCard key={c.id} c={c} />)}</div>
    </div>
  );
};

export default function AdminCommesse() {
  const [params] = useSearchParams();
  const [f, setF] = useState({ q: "", status: "aperte", payment_status: params.get("payment") || "", date_from: "", date_to: "" });
  const [list, setList] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  useEffect(() => {
    const t = setTimeout(() => api.get("/orders", { params: f }).then((r) => setList(r.data)), 250);
    return () => clearTimeout(t);
  }, [f]);

  return (
    <div>
      <AdminOverview onPayments={() => setF({ ...f, status: "aperte", payment_status: "da_effettuare" })} />
      <div id="cantieri-list-anchor" />
      <PageHeader label="Cantieri" title="Cantieri">
        <a href={`${BACKEND}/api/orders/export.xlsx?${new URLSearchParams({ ...f, token: getToken() })}`} data-testid="orders-export-button"
          className="rp-btn bg-[#070707] text-white hover:bg-black/80"><FileSpreadsheet className="h-4 w-4" /> Esporta Excel ({list ? list.length : 0})</a>
      </PageHeader>
      <div className="rp-card p-4 grid sm:grid-cols-2 lg:grid-cols-[2fr_1fr_1fr_1fr_1fr] gap-3 mb-5" data-testid="orders-filters">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#757575]" />
          <input value={f.q} onChange={set("q")} placeholder="Cliente, email, telefono o codice" className="rp-field pl-11 bg-white" data-testid="orders-search-input" />
        </div>
        <select value={f.status} onChange={set("status")} className="rp-field bg-white" data-testid="orders-status-filter">
          <option value="aperte">Aperti</option><option value="tutte">Tutti gli stati</option>
          {ORDER_STEPS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={f.payment_status} onChange={set("payment_status")} className="rp-field bg-white" data-testid="orders-payment-filter">
          <option value="">Saldo: tutti</option><option value="da_effettuare">Da effettuare</option><option value="effettuato">Effettuato</option>
        </select>
        <input type="date" value={f.date_from} onChange={set("date_from")} className="rp-field bg-white" title="Contratto dal" data-testid="orders-date-from" />
        <input type="date" value={f.date_to} onChange={set("date_to")} className="rp-field bg-white" title="Contratto al" data-testid="orders-date-to" />
      </div>
      {!list ? <Spinner /> : !list.length ? <Empty icon={HardHat} title="Nessun cantiere trovato" text="I cantieri nascono da un preventivo accettato o trasformato." data-testid="orders-empty" /> : (
        <><Totals list={list} /><div className="space-y-2" data-testid="orders-list">{list.map((o) => <OrderRow key={o.id} o={o} />)}</div></>
      )}
      <Unlinked />
    </div>
  );
}
